import cx from 'classnames'
import React, { useRef, useState } from 'react'

import { get } from '@js/common/api'
import { useToggle } from '@js/common/hooks/useToggle'

import CTA from '@f/elements/CTA/CTA'
import Icon from '@f/elements/Icon'

import { Nullable } from '@f-fertighaus/applications/Next/types/utility'
import {
  RegionalAgentInfo,
  RegionalOfficeInfo
} from '@f-fertighaus/applications/SearchResults/partials/items/Types'
import { errorLogger } from '@f-fertighaus/utils/errorLogger'

interface VendorSalesInfoProps {
  postcode: string
  nid: string
  onLocationNotSetAction: () => void
  redirectUrl?: string
}

type SalesInfoDataStatus =
  | 'location-set'
  | 'location-not-set'
  | 'loading'
  | 'has-agent-info'
  | 'has-only-office-info'
  | 'has-no-info'

interface VendorSalesInfoResponse {
  regional_office: RegionalOfficeInfo
  regional_agent: RegionalAgentInfo
}

interface VendorSalesInfoData<
  Status extends SalesInfoDataStatus,
  Response extends Nullable<VendorSalesInfoResponse>
> {
  status: Status
  response: Response
  requestFailed: boolean
}

type VendorSalesInfoState =
  | VendorSalesInfoData<'location-set' | 'location-not-set' | 'loading', null>
  | VendorSalesInfoData<
      'has-agent-info' | 'has-only-office-info' | 'has-no-info',
      VendorSalesInfoResponse
    >

export const VendorSalesInfo = ({
  nid,
  postcode,
  onLocationNotSetAction,
  redirectUrl = ''
}: VendorSalesInfoProps) => {
  const initialStatus = useRef<'location-set' | 'location-not-set'>(
    postcode ? 'location-set' : 'location-not-set'
  )
  const [state, setState] = useState<VendorSalesInfoState>({
    status: initialStatus.current,
    response: null,
    requestFailed: false
  })

  const fetchSalesAgentsInfo = () => {
    setState(current => ({ ...current, status: 'loading', response: null }))
    get<VendorSalesInfoResponse>(
      `sales-agents?vendor_nid=${nid}&display_delivery_regions__contains=${postcode}`
    )
      .then(data => {
        let status: SalesInfoDataStatus
        if (data.regional_agent) {
          status = 'has-agent-info'
        } else if (data.regional_office) {
          status = 'has-only-office-info'
        } else {
          status = 'has-no-info'
        }

        setState({
          response: data,
          status: status,
          requestFailed: false
        })
      })
      .catch(e => {
        setState({
          status: initialStatus.current,
          response: null,
          requestFailed: true
        })
        errorLogger(e)
      })
  }

  if (state.status === 'location-set' || state.status === 'location-not-set') {
    return (
      <div className='f-w-100 f-m-t-010'>
        <SalesInfoNotFetched
          variant={state.status}
          onLocationSetAction={fetchSalesAgentsInfo}
          onLocationNotSetAction={onLocationNotSetAction}
        />
        {state.requestFailed && (
          <div className='f-c-fo-err f-t-mini'>Ein Fehler ist aufgetreten</div>
        )}
      </div>
    )
  }

  if (state.status === 'has-agent-info') {
    return (
      <SalesAgentInfo
        agentData={state.response.regional_agent}
        officeData={state.response.regional_office}
      />
    )
  }

  if (state.status === 'has-only-office-info') {
    return <SalesOfficeInfo data={state.response.regional_office} />
  }

  if (state.status === 'has-no-info') {
    return <SalesOfficeNoInfo url={redirectUrl} />
  }

  return <SalesInfoSkeletonLoader />
}

interface SalesInfoNotFetchedProps {
  onLocationSetAction: () => void
  onLocationNotSetAction: () => void
  variant: 'location-set' | 'location-not-set'
}

const SalesInfoNotFetched = ({
  onLocationSetAction,
  onLocationNotSetAction,
  variant
}: SalesInfoNotFetchedProps) => {
  const onClickAction = {
    'location-set': onLocationSetAction,
    'location-not-set': onLocationNotSetAction
  }[variant]

  const actionText = {
    'location-set': 'Lokale Info anzeigen',
    'location-not-set': 'Bauort festlegen'
  }[variant]

  return (
    <div className='f-d-flr f-p-a-010 f-m-t-010 f-w-100 f-fl-gap-020 f-c-ba-bac025 SearchResultsTile-sales-agent-info'>
      <Icon
        icon='author-64px-fill'
        className='f-icon-050 f-c-fi-pri f-no-shrink'
      />
      <div className='f-d-flc'>
        <div className='f-t-mini f-t-bold'>
          Finden Sie Ihren lokalen Bauberater
        </div>
        <CTA
          tag='div'
          type='text-primary'
          target='_blank'
          className='f-t-mini'
          onClick={onClickAction}>
          {actionText}
        </CTA>
      </div>
    </div>
  )
}

interface SalesAgentInfoProps {
  agentData: RegionalAgentInfo
  officeData: RegionalOfficeInfo
}

const SalesAgentInfo = ({ agentData, officeData }: SalesAgentInfoProps) => {
  const [isUnfolded, toggleUnfolded] = useToggle(false)

  return (
    <div>
      <div className='f-d-flr f-p-a-010 f-m-t-010 f-w-100 f-fl-gap-020 f-c-ba-bac025 SearchResultsTile-sales-agent-info'>
        {agentData.avatar ? (
          <AgentAvatar avatar={agentData.avatar} />
        ) : (
          <Icon
            icon='author-64px-fill'
            className='f-icon-050 f-c-fi-pri f-no-shrink'
          />
        )}
        <div className='f-d-flc f-w-100'>
          <div className='f-t-mini f-t-bold'>{officeData.office_name}</div>
          <div className='f-d-flr f-fl-jc-sb f-fl-al-ce f-w-100'>
            <div className='f-t-mini'>{agentData.name}</div>
            <CTA
              tag='button'
              type='text-primary'
              onClick={toggleUnfolded}
              className='f-m-r-005'>
              <Icon
                icon='down-arrow-16px'
                className={cx(
                  'f-icon-020 f-c-st-pri f-c-fo-pri SearchResultsTile-sales-info-arrow',
                  isUnfolded && 'SearchResultsTile-sales-info-arrow--unfolded'
                )}
              />
            </CTA>
          </div>
        </div>
      </div>
      {isUnfolded && (
        <div className='f-d-flc f-p-h-010 f-p-b-010 f-c-ba-bac025'>
          {officeData.address_line_1 && (
            <div className='f-t-mini f-c-fo-neu200 f-m-b-005'>
              {`${officeData.address_line_1}${
                officeData.postal_code && `, ${officeData.postal_code}`
              }${officeData.city && `, ${officeData.city}`}`}
            </div>
          )}
          {agentData.phone && (
            <CTA
              tag='a'
              type='text-primary'
              target='_blank'
              href={`tel:${agentData.phone}`}
              className='f-d-flr f-fl-jc-st f-fl-al-ce'>
              <Icon
                icon='phone-16px'
                className='f-icon-020 f-c-st-ter f-m-r-010 f-no-shrink'
              />
              {agentData.phone}
            </CTA>
          )}
        </div>
      )}
    </div>
  )
}

const AgentAvatar = ({ avatar }: { avatar: string }) => (
  <div className='SearchResultsTile-sa-info-portrait'>
    <img src={avatar} alt={avatar} className='f-bo-round' />
  </div>
)

interface SalesOfficeInfoProps {
  data: RegionalOfficeInfo
}

const SalesOfficeInfo = ({ data }: SalesOfficeInfoProps) => {
  const [isUnfolded, toggleUnfolded] = useToggle(false)

  return (
    <div>
      <div className='f-d-flr f-p-a-010 f-m-t-010 f-w-100 f-fl-gap-020 f-c-ba-bac025 SearchResultsTile-sales-agent-info'>
        <Icon
          icon='author-64px-fill'
          className='f-icon-050 f-c-fi-pri f-no-shrink'
        />
        <div className='f-d-flc f-w-100'>
          <div className='f-t-mini f-t-bold'>{data.office_name}</div>
          <div className='f-d-flr f-fl-jc-sb f-fl-al-ce f-w-100'>
            <div className='f-t-mini f-c-fo-neu200'>
              {data.address_line_1}, {data.postal_code}
            </div>
            <CTA tag='button' type='text-primary' onClick={toggleUnfolded}>
              <Icon
                icon='down-arrow-16px'
                className={cx(
                  'f-icon-020 f-c-fo-pri SearchResultsTile-sales-info-arrow',
                  isUnfolded && 'SearchResultsTile-sales-info-arrow--unfolded'
                )}
              />
            </CTA>
          </div>
        </div>
      </div>
      {isUnfolded && (
        <div className='f-d-flc f-p-h-010 f-p-b-010 f-c-ba-bac025'>
          {data.address_line_1 && (
            <div className='f-t-mini f-c-fo-neu200 f-m-b-005'>
              {`${data.address_line_1}${
                data.postal_code && `, ${data.postal_code}`
              }${data.city && `, ${data.city}`}`}
            </div>
          )}
          {data.office_phone && (
            <CTA
              tag='a'
              type='text-primary'
              target='_blank'
              href={`tel:${data.office_phone}`}
              className='f-d-flr f-fl-jc-st f-fl-al-ce'>
              <Icon
                icon='phone-16px'
                className='f-icon-020 f-m-r-010 f-no-shrink'
              />
              {data.office_phone}
            </CTA>
          )}
        </div>
      )}
    </div>
  )
}

const SalesInfoSkeletonLoader = () => (
  <div className='f-d-flr f-p-a-010 f-m-t-010 f-w-100 f-fl-gap-020 f-c-ba-bac025 SearchResultsTile-sales-agent-info'>
    <div className='SearchResultsTile-sa-info-portrait SearchResultsTile-sa-info-portrait--placeholder' />
    <div className='f-d-flc f-h-100 f-fl-gap-005'>
      <div className='f-m-b-010 SearchResultsTile-sa-info-title-placeholder' />
      <div className='SearchResultsTile-sa-info-action-placeholder' />
    </div>
  </div>
)

const SalesOfficeNoInfo = ({ url }: { url: string }) => {
  return (
    <div>
      <div className='f-d-flr f-m-t-010 f-p-a-010 f-w-100 f-fl-gap-020 f-c-ba-bac025 SearchResultsTile-sales-agent-info'>
        <Icon
          icon='author-64px-fill'
          className='f-icon-050 f-c-fi-pri f-no-shrink'
        />
        <div className='f-d-flc f-w-100'>
          <div className='f-t-mini f-t-bold'>
            Keine lokalen Büro-Daten gefunden
          </div>
          <CTA
            tag='a'
            type='text-primary'
            target='_blank'
            href={url}
            onClick={() => {
              window.location.href = url
            }}
            className='f-d-flr f-fl-jc-st f-fl-al-ce f-t-mini'>
            Kontakt zum lokalen Berater anfragen
          </CTA>
        </div>
      </div>
    </div>
  )
}
